import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { cn } from "~/lib/cn";
import type { RouteKey } from "~/config/route-config";
import type { NavSessionUser } from "~/lib/nav-session";
import { resolveRouteFromPathname } from "~/lib/locale-path";
import { Button } from "~/components/ui";
import { AppLink } from "~/components/ui/AppLink";
import { LanguageSwitcher } from "~/components/ui/LanguageSwitcher";
import { IconMenu } from "~/components/layout/nav-icons";
import { AvatarMenu } from "~/components/layout/AvatarMenu";
import { Wordmark } from "~/components/layout/Wordmark";
import { useHeaderScrollState } from "~/components/layout/use-header-scrolled";
import { useAutotestExit } from "~/components/autotest/AutotestExit";

interface AppHeaderProps {
  /** Open the mobile cúter drawer. */
  onMenuOpen: () => void;
  /** Drawer open state — drives aria-expanded on the menu button and pins the bar. */
  menuOpen: boolean;
  /**
   * T102 (T226 D1 deferral) — route renders a dark full-bleed hero: the bar
   * takes the `.dark` treatment at top and snaps to the light scrolled
   * treatment past the threshold (`.over-dark` scopes out the bg fade).
   */
  darkAtTop?: boolean;
  /**
   * T872 S1 — a signed-in member on a public route keeps this header (R-T872-3):
   * same four links, «Mi espacio» disclosure where a guest sees «Iniciar sesión».
   */
  sessionUser?: NavSessionUser | null;
  /**
   * T872 — a funnel route (`handle.logoOnlyHeader`, the auto-test) keeps this same bar
   * with the wordmark alone: no links, language, account or menu button. Same bar, so the
   * logo never moves between pages.
   */
  logoOnly?: boolean;
  className?: string;
}

/**
 * The four public destinations, in the order the October delivery fixed for every
 * public page (R-T872-1): El método · Auto-test · Guías · Ayuda. «Ayuda» is the FAQ,
 * which offers filtered support from there (design pass, 2026-10-08).
 */
export const PUBLIC_NAV: ReadonlyArray<{ routeKey: RouteKey; labelKey: string }> = [
  { routeKey: "metodo", labelKey: "public.method" },
  { routeKey: "autoTest", labelKey: "public.self_test" },
  { routeKey: "guias", labelKey: "public.guides" },
  { routeKey: "faq", labelKey: "public.help" },
];

/**
 * Public global header — T98 rebrand `.appbar`, reshaped by T872 S1 (`.appbar--public`).
 * Desktop (≥768): wordmark · four links · language pill · «Iniciar sesión» or the
 * member's «Mi espacio» disclosure. Mobile (<768): wordmark · menu button only — the
 * cúter carries navigation, language and the account group.
 * Scroll: 12px lifts `is-scrolled` (R-T226-3), 32px compacts the desktop bar, and on a
 * phone the bar hides while scrolling down and returns on the way up (R-T872-2); it
 * stays while the cúter is open (`menuOpen` pins it) and CSS keeps it visible under
 * keyboard focus or an open panel.
 */
export function AppHeader({ onMenuOpen, menuOpen, darkAtTop, sessionUser, logoOnly = false, className }: AppHeaderProps) {
  const { t } = useTranslation("nav");
  const { pathname } = useLocation();
  const current = resolveRouteFromPathname(pathname)?.routeKey;
  const { scrolled, compact, hidden } = useHeaderScrollState({ pinned: menuOpen });
  // The funnel's logo-only bar carries its way out (F4, 2026-10-10).
  const exit = useAutotestExit(sessionUser);

  return (
    <header
      className={cn(
        "appbar appbar--public sticky top-0 z-50",
        scrolled && "is-scrolled",
        compact && "is-compact",
        hidden && "is-hidden",
        darkAtTop && "over-dark",
        darkAtTop && !scrolled && "dark",
        logoOnly && "appbar--logo-only",
        className,
      )}
    >
      <div className="left">
        {/* T634 — `/es` sends a signed-in member to her panel unless `?public=1`; the
            public chrome's logo means «Inicio», so a member takes the public access. */}
        <Wordmark
          routeKey="home"
          search={sessionUser ? "?public=1" : undefined}
          aria-label={t("home_aria")}
          onClick={logoOnly ? exit.guard : undefined}
        />
      </div>

      {logoOnly ? (
        <>
          {exit.control}
          {exit.dialog}
        </>
      ) : (
        <>
          <nav className="links" aria-label={t("primary_label")}>
            {PUBLIC_NAV.map(({ routeKey, labelKey }) => (
              <AppLink
                key={routeKey}
                routeKey={routeKey}
                className="link"
                aria-current={current === routeKey ? "page" : undefined}
              >
                {t(labelKey)}
              </AppLink>
            ))}
          </nav>

          <div className="right">
            <LanguageSwitcher variant="text" />
            {sessionUser ? (
              <AvatarMenu sessionUser={sessionUser} publicContext />
            ) : (
              <AppLink
                routeKey="signup"
                className={cn("btn-public-secondary is-compact appbar-cta", darkAtTop && !scrolled && "on-dark")}
              >
                {t("sign_in")}
              </AppLink>
            )}
          </div>

          <Button
            type="button"
            variant="icon"
            className="bare menu-toggle"
            data-menu-toggle
            aria-label={t("menu_open")}
            aria-expanded={menuOpen}
            onClick={onMenuOpen}
          >
            <IconMenu />
          </Button>
        </>
      )}
    </header>
  );
}
